import type { Source } from '@epimodev/callbag-doki';
import zip from '@epimodev/callbag-doki/sources/zip';
import of from '@epimodev/callbag-doki/sources/of';
import pipeSource from '@epimodev/callbag-doki/utils/pipe';
import map from '@epimodev/callbag-doki/operators/map';
import flatMap from '@epimodev/callbag-doki/operators/flatMap';
import catchError from '@epimodev/callbag-doki/operators/catchError';
import httpRequest from '@epimodev/callbag-doki/sources/httpRequest';

import type { ConfigContextState } from 'app/common/ConfigContext';
import { getMetricsSuffix } from 'app/config/metrics.config.helper';
import { isError } from 'app/utils/errors';
import { BLOCK_QUERY_MAP, PERIODS_URL } from 'app/config/urlParams.config';
import { hasDetailedStats } from 'app/config/videogameSpecific.config.helper';
import {
  getBasicAndDetailedBlocks,
  getSingleAndDuplicatedBlocksToFetch,
  isBlockWithPeriod
} from 'app/config/urlParams.config.helper';
import { httpCache } from 'app/utils/cache';
import { ENTITY_URL_PART, getBackendUrl, getVideogameUrl } from 'app/utils/api_url';
import { ERROR_NOT_FOUND, ERROR_TO_FILTER, ERROR_UNKNOWN } from 'app/constants/errors';

import {
  getInitialComposedEntityData,
  getSeriesIdFromComposedData,
  getVideogameFromComposedData
} from './data';

interface FetchUrls {
  primary: string | null;
  secondary: string | null;
}

const getFetchUrls = (
  config: ConfigContextState,
  currentData: ComposedEntityData | undefined
): FetchUrls => {
  const backendUrl = getBackendUrl();
  const { entity, id } = config;

  let primary: string | null = null;
  let secondary: string | null = null;

  if (backendUrl && entity && id !== undefined) {
    primary = `${backendUrl}/api/${ENTITY_URL_PART[entity]}/${id}`;

    if (entity === 'match') {
      const seriesId = getSeriesIdFromComposedData(currentData);
      const videogame = getVideogameFromComposedData(currentData);
      if (seriesId) {
        secondary = `${backendUrl}/api/${getVideogameUrl(videogame)}/series/${seriesId}`;
      }
    }
  }

  return { primary, secondary };
};

const loadEntityBlock = (
  block: Block,
  blockToLoad: string,
  fetchUrl: string | null,
  period: string | undefined,
  userMetricSuffix: string
): Source<[Block, BlockData | Error]> =>
  pipeSource(
    httpRequest<BlockData>({
      url: `${fetchUrl}/${period ? `${period}/` : ''}${blockToLoad.replaceAll(
        '-',
        '_'
      )}${userMetricSuffix}`,
      cache: httpCache
    }),
    map(response => {
      const formatedResponse: [Block, BlockData | Error] = [block, response.data];
      return formatedResponse;
    }),
    catchError(e => {
      if (e.status === 404) {
        return of([block, new Error(ERROR_NOT_FOUND)]);
      }
      if (e.status === 0) {
        return of([block, new Error(ERROR_TO_FILTER)]);
      }
      let error = '';
      try {
        error = JSON.stringify(e);
      } catch {
        error = 'unable to have error details';
      }
      return of([block, new Error(`Error in loading data of block ${block}: ${error}`)]);
    })
  );

const loadEntityBlocks = (
  blocks: Block[],
  fetchUrls: FetchUrls,
  period: string | undefined,
  userMetricSuffix: string
) =>
  blocks.map(block => {
    const blockToLoad = BLOCK_QUERY_MAP[block] || block;

    const withPeriod = isBlockWithPeriod(blockToLoad);
    const fetchUrl =
      blockToLoad.startsWith('series-') && fetchUrls.secondary
        ? fetchUrls.secondary
        : fetchUrls.primary;

    return loadEntityBlock(
      block,
      // If we load from the series endpoint, we need to remove the 'series-' prefix
      fetchUrl === fetchUrls.secondary ? blockToLoad.replace('series-', '') : blockToLoad,
      fetchUrl,
      withPeriod ? period : undefined,
      userMetricSuffix
    );
  });

const assignBlockData = <T extends Block>(
  entityData: ComposedEntityData,
  block: T,
  response: BlockData | Error
) => {
  if (isError(response) && response.message === ERROR_TO_FILTER) {
    return;
  }
  // This cast is safe because we're ensuring T is a Block
  entityData[block] = response as ComposedEntityData[T];
};

const alreadyHasData = (data: ComposedEntityData | undefined): boolean => {
  if (!data) return false;
  return Object.values(data).some(value => value !== null);
};

const loadEntity = (
  config: ConfigContextState,
  blocksToFetch: Block[],
  displayedTab: Tab | null,
  period: StatPeriod,
  currentData: ComposedEntityData | undefined
): Source<ComposedEntityData | Error> => {
  const userMetricSuffix = getMetricsSuffix(config, displayedTab);
  const periodUrl = PERIODS_URL[period];
  const fetchUrls = getFetchUrls(config, currentData);

  // We put aside the blocks that are based on other blocks, to load them last.
  // They will be put aside only if the block on which they depends is already loaded
  // That way, when we load them, the data will come from the httpCache
  const [singleBlocksToFetch, duplicatedBlocksToFetch] =
    getSingleAndDuplicatedBlocksToFetch(blocksToFetch);

  const [basicBlocks, detailedBlocks] = getBasicAndDetailedBlocks(
    config.entity,
    singleBlocksToFetch
  );

  return pipeSource(
    zip(
      // If the requested blocks are not basic ones, we fake load an error name block just to pass the zip
      basicBlocks.length
        ? loadEntityBlocks(basicBlocks, fetchUrls, periodUrl, userMetricSuffix)
        : [of<[Block, Error]>(['name', new Error(ERROR_TO_FILTER)])] // This is a fake block so we filter it in for the widget display metrics
    ),
    flatMap(responses => {
      // We first look at the data for the basic blocks, to determine the videogame and see if we need to load detailed blocks
      // That will allow to avoid loading detailed blocks if we don't need them
      const basicBlocksData = Object.fromEntries(responses);
      const videogame = getVideogameFromComposedData(basicBlocksData as ComposedEntityData);

      // If there are detailed blocks, we check if we need to load them
      // Either the videogame game (known from the basic blocks data) has detailed stats, or all the basic blocks have failed
      // In that case, we load the detailed blocks
      if (
        detailedBlocks.length > 0 &&
        ((videogame && hasDetailedStats(videogame)) ||
          responses.every(([_, data]) => isError(data) || !data))
      ) {
        // return the basic blocks data we already have + load the detailed blocks
        return zip([
          ...responses.map(res => of(res)),
          ...loadEntityBlocks(detailedBlocks, fetchUrls, periodUrl, userMetricSuffix)
        ]);
      }
      // return the basic blocks data we already have
      return zip(responses.map(res => of(res)));
    }),
    flatMap(responses =>
      // We load the put aside blocks that are based on other blocks
      zip([
        ...responses.map(res => of(res)),
        ...loadEntityBlocks(duplicatedBlocksToFetch, fetchUrls, periodUrl, userMetricSuffix)
      ])
    ),
    map(responses => {
      // Check if we already have data
      // If we do, it means we're using a tab system and we don't want to override the data of the other tabs
      if (!alreadyHasData(currentData)) {
        // If all calls have resulted in an error, we return a global error
        const allInErrors = responses.every(([_, data]) => isError(data) || !data);
        if (allInErrors) {
          const allNotFound = responses.every(
            ([_, error]) => isError(error) && error.message === ERROR_NOT_FOUND
          );
          if (allNotFound) {
            return new Error(ERROR_NOT_FOUND);
          }
          return new Error(ERROR_UNKNOWN);
        }
      }

      // Else we return the composed data
      const entityData = getInitialComposedEntityData();
      responses.forEach(([block, response]) => {
        assignBlockData(entityData, block, response);
      });
      return entityData;
    })
  );
};

const updateEntityData = <T extends ComposedEntityData>(currentData: T | undefined, newData: T) => {
  if (!currentData) {
    return newData;
  }
  const updatedData = { ...currentData };
  (Object.keys(newData) as Array<keyof T>).forEach(key => {
    if (newData[key] !== null) {
      updatedData[key] = newData[key];
    }
  });
  return updatedData;
};

export { loadEntity, updateEntityData };
